import { restoreStoredFeatures } from './local-dataset-migration.js';
import { retainDataset } from './local-dataset-store.js';
import type { SchemaInput } from '../shared/schema-input.js';
import { isString } from '../shared/runtime-values.js';
import { z } from 'zod';
import { dataLayerPayload, importSourceFileSchema, type DataLayer, type DataFeature } from '../shared/data-layer.js';
import { jsonByteLength } from '../shared/json-size.js';

export const DATA_RECORD_BYTES = 2 * 1024 * 1024;
export const DATA_CHUNKS_STORE = 'chunks';
const sourceSchema = importSourceFileSchema.pick({ name: true, kml: true }).extend({ encoding: z.enum(['base64', 'blob']), chunks: z.array(z.string()) });
const datasetReferenceSchema = z.object({ id: z.string(), count: z.number(), columns: z.array(z.string()) });
const manifestSchema = z.object({ format: z.literal(2), dataset: datasetReferenceSchema.optional(), metadata: z.unknown(), chunks: z.array(z.union([z.string(), z.array(z.string())])), sources: z.array(sourceSchema) });
const chunkSchema = z.object({ expiresAt: z.number(), data: z.union([z.string(), z.instanceof(Blob)]) });

function read(db: IDBDatabase, store: string, id: string): Promise<SchemaInput> {
  return new Promise((resolve, reject) => {
    const request = db.transaction(store).objectStore(store).get(id);
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

export function writeDataRecords(db: IDBDatabase, store: string, records: Array<{ id: string; expiresAt: number; [key: string]: SchemaInput }>): Promise<void> {
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(store, 'readwrite');
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error);
    transaction.onabort = () => reject(transaction.error ?? new Error('Local storage write stopped.'));
    try { for (const record of records) transaction.objectStore(store).put(record); }
    catch (error) { transaction.abort(); reject(error); }
  });
}

async function digest(bytes: Uint8Array<ArrayBuffer>): Promise<string> {
  return Array.from(new Uint8Array(await crypto.subtle.digest('SHA-256', bytes)), byte => byte.toString(16).padStart(2, '0')).join('');
}

/** Only bounded records cross the IndexedDB structured-clone boundary. */
export async function writeLayerRecords(db: IDBDatabase, layer: DataLayer, expiresAt: number, created: string[]) {
  if (layer.dataset) await retainDataset(layer.dataset);
  const chunks: Array<string | string[]> = [];
  const sources: z.infer<typeof sourceSchema>[] = [];
  const encoder = new TextEncoder();
  async function chunk(data: string | Blob): Promise<string> {
    const bytes = isString(data) ? encoder.encode(data) : new Uint8Array(await data.arrayBuffer());
    if (bytes.byteLength > DATA_RECORD_BYTES) throw new Error('A data record exceeds the local storage limit.');
    const id = `${isString(data) ? 'text' : 'blob'}-${await digest(bytes)}`;
    const existing = await new Promise<IDBValidKey | undefined>((resolve, reject) => {
      const request = db.transaction(DATA_CHUNKS_STORE).objectStore(DATA_CHUNKS_STORE).getKey(id);
      request.onsuccess = () => resolve(request.result); request.onerror = () => reject(request.error);
    });
    if (existing === undefined) created.push(id);
    await writeDataRecords(db, DATA_CHUNKS_STORE, [{ id, expiresAt, data }]);
    return id;
  }
  let batch: DataFeature[] = [];
  let size = 2;
  for (const feature of layer.features) {
    const bytes = jsonByteLength(feature, DATA_RECORD_BYTES);
    if (bytes + 2 > DATA_RECORD_BYTES) {
      if (batch.length) { chunks.push(await chunk(JSON.stringify(batch))); batch = []; size = 2; }
      const blob = new Blob([JSON.stringify([feature])]);
      const ids: string[] = [];
      for (let offset = 0; offset < blob.size; offset += DATA_RECORD_BYTES) ids.push(await chunk(blob.slice(offset, offset + DATA_RECORD_BYTES)));
      chunks.push(ids);
      continue;
    }
    if (size + bytes + 1 > DATA_RECORD_BYTES) {
      chunks.push(await chunk(JSON.stringify(batch)));
      batch = []; size = 2;
    }
    batch.push(feature); size += bytes + 1;
  }
  if (batch.length) chunks.push(await chunk(JSON.stringify(batch)));
  for (const source of layer.sourceFiles ?? []) {
    const ids: string[] = [];
    for (let start = 0; start < source.base64.length; start += DATA_RECORD_BYTES) ids.push(await chunk(source.base64.slice(start, start + DATA_RECORD_BYTES)));
    sources.push({ name: source.name, kml: source.kml, encoding: 'base64', chunks: ids });
  }
  for (const source of layer.retainedSources ?? []) {
    const ids: string[] = [];
    for (let start = 0; start < source.data.size; start += DATA_RECORD_BYTES) ids.push(await chunk(source.data.slice(start, start + DATA_RECORD_BYTES)));
    sources.push({ name: source.name, encoding: 'blob', chunks: ids });
  }
  const { sourceFiles: _sources, ...metadata } = dataLayerPayload(layer).layer;
  const manifest = { format: 2, metadata, chunks, sources, dataset: layer.dataset };
  const id = await digest(encoder.encode(JSON.stringify(manifest)));
  return { id, expiresAt, manifest };
}

export async function readLayerRecords(db: IDBDatabase, value: SchemaInput): Promise<DataLayer | null> {
  const result = manifestSchema.safeParse(value);
  if (!result.success) return null;
  const { metadata, chunks, sources } = result.data;
  async function chunk(id: string) {
    const parsed = chunkSchema.safeParse(await read(db, DATA_CHUNKS_STORE, id));
    if (!parsed.success || parsed.data.expiresAt <= Date.now()) throw new Error('A local data record is missing or expired.');
    return parsed.data.data;
  }
  async function* batches(): AsyncGenerator<SchemaInput[]> {
    for (const id of chunks) {
      let text: string;
      if (Array.isArray(id)) {
        const parts: Blob[] = [];
        for (const partId of id) { const data = await chunk(partId); if (!(data instanceof Blob)) throw new Error('Invalid local record.'); parts.push(data); }
        text = await new Blob(parts).text();
      } else {
        const data = await chunk(id);
        if (!isString(data)) throw new Error('Invalid local record.');
        text = data;
      }
      const items: SchemaInput = JSON.parse(text);
      if (!Array.isArray(items)) throw new Error('Invalid local feature records.');
      yield items;
    }
  }
  const layer = await restoreStoredFeatures(metadata, batches(), result.data.dataset);
  if (!layer) return null;
  for (const source of sources) {
    if (source.encoding === 'blob') {
      const parts: Blob[] = [];
      for (const id of source.chunks) {
        const data = await chunk(id);
        if (!(data instanceof Blob)) return null;
        parts.push(data);
      }
      (layer.retainedSources ??= []).push({ name: source.name, data: new Blob(parts) });
    } else {
      const parts: string[] = [];
      for (const id of source.chunks) {
        const data = await chunk(id);
        if (!isString(data)) return null;
        parts.push(data);
      }
      const file = importSourceFileSchema.safeParse({ name: source.name, kml: source.kml, base64: parts.join('') });
      if (!file.success) return null;
      (layer.sourceFiles ??= []).push(file.data);
    }
  }
  return layer;
}

export function removeDataRecords(db: IDBDatabase, ids: string[]): Promise<void> {
  if (!ids.length) return Promise.resolve();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(DATA_CHUNKS_STORE, 'readwrite');
    for (const id of ids) transaction.objectStore(DATA_CHUNKS_STORE).delete(id);
    transaction.oncomplete = () => resolve();
    transaction.onabort = () => reject(transaction.error);
    transaction.onerror = () => reject(transaction.error);
  });
}

export function withDataStoreLock<T>(name: string, run: () => Promise<T>): Promise<T> {
  return globalThis.navigator?.locks ? navigator.locks.request(name, run) : run();
}
